import { useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useItemBookmark } from './ItemBookmarkContext'
import { openItemInCode } from '../code/openInCode'
import { standardName } from '../../utils/standardRef'
import './ReferenceChips.css'

const IconOpenCode = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
)

// Same tag as the library nav: hollow until the item is bookmarked, then filled.
const IconBookmark = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
)

// Collapsed height cap (px); keep in sync with the `.ref-chips-list.is-collapsed`
// max-height in ReferenceChips.css.
const COLLAPSED_MAX = 68

// A list of clickable item chips ("{label} · p.{pageStart}") with a See more/less
// toggle that only appears when the chips actually overflow the collapsed height.
//
// Shared by assistant-answer references (chat) and text-search covering items, so
// the two look identical. Clicking a chip calls onOpenItem(item).
//
// `groupBy` turns the flat list into a grouped list: it should be a function
// `(item) => groupKey` (e.g. `(item) => item.standard`). Items are grouped by
// that key and a small heading is rendered per group; the chips sit under each
// heading. The outer "References" label still applies.
function ReferenceChips({ items, onOpenItem, label = 'References', groupBy }) {
  const bookmarks = useItemBookmark()
  const navigate = useNavigate()
  const listRef = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)

  // scrollHeight is the full content height even while clipped by max-height, so
  // this stays correct in both states. Re-measured on width changes (chips
  // reflow) via a ResizeObserver.
  useLayoutEffect(() => {
    const el = listRef.current
    if (!el) return undefined
    const measure = () => setOverflowing(el.scrollHeight > COLLAPSED_MAX + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [items])

  if (!items || items.length === 0) return null

  const collapsed = overflowing && !expanded

  const chip = (item) => {
    const title = `${standardName(item.standard) || 'Standard'} — ${item.label}`
    const page =
      item.pageStart != null ? <span className="ref-chip-page">· p.{item.pageStart}</span> : null
    const text = (
      <>
        <span className="ref-chip-label">{item.label}</span>
        {page}
      </>
    )
    if (!bookmarks) {
      return (
        <button
          key={item._id}
          type="button"
          className="ref-chip"
          title={title}
          onClick={() => onOpenItem?.(item)}
          disabled={!onOpenItem}
        >
          {text}
        </button>
      )
    }
    const bookmarked = bookmarks.isBookmarked(item._id)
    return (
      <div key={item._id} className="ref-chip has-bookmark" title={title}>
        <button
          type="button"
          className="ref-chip-open"
          onClick={() => onOpenItem?.(item)}
          disabled={!onOpenItem}
        >
          {text}
        </button>
        <button
          type="button"
          className={'ref-chip-bookmark' + (bookmarked ? ' is-on' : '')}
          title={bookmarked ? 'Edit bookmark' : 'Bookmark'}
          aria-label={bookmarked ? 'Edit bookmark' : 'Bookmark'}
          aria-pressed={Boolean(bookmarked)}
          onClick={() => bookmarks.bookmarkItem(item)}
        >
          <IconBookmark filled={bookmarked} />
        </button>
        <button
          type="button"
          className="ref-chip-jump"
          title="Open in Code"
          aria-label="Open in Code"
          onClick={() => {
            if (openItemInCode(item)) navigate('/code')
          }}
        >
          <IconOpenCode />
        </button>
      </div>
    )
  }

  const renderBody = () => {
    if (!groupBy) {
      return items.map(chip)
    }
    const groups = new Map()
    items.forEach((item) => {
      const key = groupBy(item) || 'Uncategorized'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(item)
    })
    return Array.from(groups.entries()).map(([key, groupItems]) => (
      <div key={key} className="ref-chips-group">
        <div className="ref-chips-group-title">{key}</div>
        <div className="ref-chips-group-chips">{groupItems.map(chip)}</div>
      </div>
    ))
  }

  return (
    <div className="ref-chips">
      {label ? <div className="ref-chips-label">{label}</div> : null}
      <div
        ref={listRef}
        className={`ref-chips-list${groupBy ? ' is-grouped' : ''}${collapsed ? ' is-collapsed' : ''}`}
      >
        {renderBody()}
      </div>
      {overflowing && (
        <button type="button" className="ref-chips-toggle" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  )
}

export default ReferenceChips
